/* eslint-disable max-lines */
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Input,
  ScrollShadow,
  Skeleton,
} from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useHeroToast } from '@/hooks';
import { useOrganizationState, useUserState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';

import { LOGO } from '../../config';
import { usePostChangeUserOrganization, useQueryHelper } from '../../queries';
import { useGetOrganizationsByIds } from '../../queries/useGetOrganizationsByIds';
import { getRequestErrorMessage, inputProps } from '../../utils';
import { getImageUrl } from '../../utils/getImageUrl';
import {
  BlockingOverlay,
  CloseButton,
  EmptyState,
  LogoWrapper,
  MenuItem,
  ModalCard,
  ModalCardBody,
  ModalCardFooter,
  ModalCardHeader,
  NoOrgCard,
  OrgInfo,
  OrgName,
  OrgSelectorContainer,
  PrimaryButton,
  StaticOrgDisplay,
  TriggerButton,
} from './OrganizationSelector.styled';

type OrgWithLogo = { key: string; label: string; logoUrl?: string };

const LogoBox: FC<{ src?: string; alt: string; size?: number }> = ({ src, alt, size = 40 }) => {
  const initials = (alt?.trim()?.[0] ?? '?').toUpperCase();
  const [hasTransparency, setHasTransparency] = useState<boolean>(false);

  const checkImageTransparency = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;

    // Create a canvas to analyze the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Use smaller canvas for performance
      const sampleSize = 50;
      canvas.width = Math.min(img.naturalWidth, sampleSize);
      canvas.height = Math.min(img.naturalHeight, sampleSize);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      try {
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Sample points for transparency (check every 10th pixel for performance)
        for (let i = 3; i < data.length; i += 40) { // Check every 10th pixel (4 * 10)
          if (data[i] < 255) {
            setHasTransparency(true);
            return;
          }
        }
        setHasTransparency(false);
      } catch (error) {
        // If CORS error or other issue, assume gray background for safety
        setHasTransparency(true);
      }
    }
  };

  // Determine background classes based on transparency
  const bgClass = hasTransparency
    ? 'bg-gray-100 dark:bg-gray-700'
    : 'bg-white dark:bg-gray-800';

  return (
    <div
      className={`flex items-center justify-center text-[12px] text-center rounded-[6px] text-[#64748b] dark:text-[#94a3b8] shadow-sm border border-[#dcdfe4] dark:border-[#4b5563] overflow-hidden ${bgClass}`}
      style={{
        width: size,
        height: size
      }}
      title={alt}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className="object-contain p-2"
          style={{ width: '100%', height: '100%' }}
          onLoad={checkImageTransparency}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
            (e.currentTarget.parentElement as HTMLElement).textContent = initials;
          }}
          crossOrigin="anonymous"
        />
      ) : (
        initials
      )}
    </div>
  );
};

interface OrganizationSelectorProps {
  isCollapsed: boolean;
}

export const OrganizationSelector: FC<OrganizationSelectorProps> = ({ isCollapsed }) => {
  const { t } = useTranslation();
  const { currentUser, logout } = useUserState();
  const { organizationId, plan } = useOrganizationState();
  const { organizations, isResolved: orgResolved } = useOrganizationState();

  const organizationsQuery = useGetOrganizationsByIds(organizations);
  const { data: orgs } = organizationsQuery;
  const { isLoading: isOrgsLoading, isError: isOrgsError } = useQueryHelper({ queries: [organizationsQuery] });


  const { showSuccess, showError } = useHeroToast();
  const { mutate: changeOrg, isPending } = usePostChangeUserOrganization();
  const navigate = useLocaleNavigate();
  const queryClient = useQueryClient();

  const [selectedKey, setSelectedKey] = useState<string | null>(organizationId);
  const [tempSelectedKey, setTempSelectedKey] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Określamy stany użytkownika względem organizacji
  const isUserOrgEmpty = !organizationId || organizationId.trim() === '';
  const hasUserOrganization = !isUserOrgEmpty;

  // Sprawdzamy czy auth state jest w pełni załadowany
  const isAuthLoaded = currentUser !== null && organizations !== undefined;

  // Czekamy na pełne załadowanie danych
  const isDataReady = orgResolved && !isOrgsLoading && isAuthLoaded;

  // Case 1: User nie ma żadnych organizacji i nie ma aktywnej
  const hasNoOrganizations = !!(
    isDataReady
      && isUserOrgEmpty
      && Array.isArray(organizations)
      && organizations.length === 0
  );

  // Case 2: User ma organizacje na liście, ale nie ma aktywnej
  const hasOrganizationsButNoActive = !!(
    isDataReady
      && isUserOrgEmpty
      && Array.isArray(organizations)
      && organizations.length > 0
  );

  // Case 3: User ma aktywną organizację, ale lista organizations jest pusta
  // (organizacja mogła zostać usunięta lub jest problem z synchronizacją)
  const hasActiveButNoOrganizations = !!(
    isDataReady
      && hasUserOrganization
      && Array.isArray(organizations)
      && organizations.length === 0
  );

  const shouldShowBlockingUI = hasNoOrganizations || hasOrganizationsButNoActive || hasActiveButNoOrganizations;

  useEffect(() => {
    if (organizationId && organizationId.trim() !== '') {
      setSelectedKey(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (open || hasOrganizationsButNoActive) {
      setTempSelectedKey(selectedKey);
      setSearchQuery('');
    }
  }, [open, selectedKey, hasOrganizationsButNoActive]);

  const orgMap = useMemo(() => {
    const m = new Map<string, OrgWithLogo>();
    (orgs ?? []).forEach((o) => m.set(o.key, o as OrgWithLogo));
    return m;
  }, [orgs]);

  const filteredOrgs = useMemo(() => {
    if (!orgs) return [];
    if (!searchQuery.trim()) return orgs as OrgWithLogo[];

    const query = searchQuery.toLowerCase().trim();
    return (orgs as OrgWithLogo[]).filter((org) => org.label.toLowerCase().includes(query));
  }, [orgs, searchQuery]);

  const selectedOrg = selectedKey ? orgMap.get(selectedKey) : undefined;

  const isBusy = isOrgsLoading || !orgResolved || !isAuthLoaded;
  const isMutating = isPending;
  const isCurrentOrg = tempSelectedKey === organizationId;
  const isSingleOrg = (orgs?.length ?? 0) === 1;

  const handleConfirm = () => {
    if (isBusy || isMutating || !tempSelectedKey) return;
    if (isCurrentOrg && !isUserOrgEmpty) return;

    // Ustaw stan ładowania
    useOrganizationState.setState({ isResolved: false });

    const safetyTimer = window.setTimeout(() => {
      useOrganizationState.setState({ isResolved: true });
    }, 10000);

    changeOrg(
      { userId: currentUser?.uid as string, associatedOrganization: tempSelectedKey },
      {
        onSuccess: () => {
          window.clearTimeout(safetyTimer);

          // Wyczyść cache zapytań
          queryClient.clear();

          setOpen(false);
          showSuccess(t('organizationSelector.switchSuccess'), t('common.success'));

          // Firebase onSnapshot w userState automatycznie zaktualizuje organizację
          // gdy associated_organization zmieni się w Firebase
          // Czekamy krótko na propagację zmian, potem odświeżamy stronę
          setTimeout(() => {
            // Pełne odświeżenie zapewni świeży stan wszystkich komponentów
            window.location.href = '/';
          }, 1000);
        },
        onError: (error) => {
          window.clearTimeout(safetyTimer);
          // Backend odsyła klucz i18n (np. user.organizationForbidden), helper go tłumaczy.
          showError(getRequestErrorMessage(error, t('common.error')));
          useOrganizationState.setState({ isResolved: true });
        },
      },
    );
  };

  const handleCancel = () => {
    if (hasOrganizationsButNoActive || hasActiveButNoOrganizations) return;

    setTempSelectedKey(selectedKey);
    setSearchQuery('');
    setOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const renderBlockingOverlay = () => {
    if (!shouldShowBlockingUI) return null;

    return createPortal(
      <BlockingOverlay>
        {/* Case 1: Brak organizacji i brak aktywnej */}
        {hasNoOrganizations && (
          <NoOrgCard>
            <CloseButton onClick={handleLogout} title={t('organization.logout')}>
              <XMarkIcon className="w-5 h-5" />
            </CloseButton>
            <ExclamationTriangleIcon className="w-16 h-16 text-amber-500" />
            <h2 className="text-xl font-light text-slate-800 dark:text-white mt-4">
              {t('organization.noOrganizationAssigned')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm mt-2">
              {t('organization.noOrganizationAssignedDescription')}
            </p>
          </NoOrgCard>
        )}

        {/* Case 3: Ma aktywną organizację, ale lista jest pusta (organizacja usunięta/niedostępna) */}
        {hasActiveButNoOrganizations && (
          <NoOrgCard>
            <CloseButton onClick={handleLogout} title={t('organization.logout')}>
              <XMarkIcon className="w-5 h-5" />
            </CloseButton>
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500" />
            <h2 className="text-xl font-light text-slate-800 dark:text-white mt-4">
              {t('organization.organizationUnavailable')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm mt-2">
              {t('organization.organizationUnavailableDescription')}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 font-mono">
              {`${t('organization.id')}: ${organizationId}`}
            </p>
          </NoOrgCard>
        )}

        {/* Case 2: Ma organizacje na liście, ale nie ma aktywnej */}
        {hasOrganizationsButNoActive && (
          <ModalCard>
            <ModalCardHeader>
              <div>
                <h2 className="text-lg font-light text-slate-800 dark:text-white">
                  {t('organizationSelector.selectOrganization')}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('organizationSelector.continueDescription')}
                </p>
              </div>
            </ModalCardHeader>

            <ModalCardBody>
              {(orgs?.length ?? 0) > 5 && (
              <div className="mb-3">
                <Input
                  {...inputProps}
                  variant="bordered"
                  classNames={{
                    inputWrapper: '!h-[44px] min-h-[44px] bg-white border-1 border-border-strong rounded-8 shadow-none data-[hover=true]:border-gray-400 group-data-[focus=true]:border-accent-ink',
                    input: 'text-sm',
                  }}
                  placeholder={t('organizationSelector.searchPlaceholder')}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  startContent={<MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />}
                  isClearable
                  onClear={() => setSearchQuery('')}
                />
              </div>
              )}

              {/* eslint-disable-next-line no-nested-ternary */}
              {isOrgsError ? (
                <EmptyState>
                  <ExclamationTriangleIcon className="w-10 h-10 text-red-400" />
                  <span>{t('organization.loadError', 'Błąd ładowania organizacji')}</span>
                  <button
                    type="button"
                    onClick={() => organizationsQuery.refetch()}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    {t('common.reload', 'Załaduj ponownie')}
                  </button>
                </EmptyState>
              ) : isOrgsLoading ? (
              // eslint-disable-next-line max-lines
                <div className="flex flex-col gap-2">
                  {[...Array(3)].map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="flex items-center gap-3 p-3">
                      <Skeleton className="rounded-md flex-shrink-0" style={{ width: 40, height: 40 }} />
                      <Skeleton className="h-[14px] flex-1 rounded" />
                    </div>
                  ))}
                </div>
              ) : filteredOrgs.length === 0 ? (
                <EmptyState>
                  <MagnifyingGlassIcon className="w-10 h-10 text-slate-300" />
                  <span>{t('organizationSelector.noOrganizationsFound')}</span>
                </EmptyState>
              ) : (
                <ScrollShadow className="w-full max-h-[280px]" hideScrollBar>
                  <div className="flex flex-col gap-1">
                    {filteredOrgs.map((org) => {
                      const isSelected = org.key === tempSelectedKey;

                      return (
                        <MenuItem
                          key={org.key}
                          onClick={() => setTempSelectedKey(org.key)}
                          disabled={isMutating}
                          $isActive={isSelected}
                        >
                          <LogoWrapper>
                            <LogoBox
                              src={getImageUrl(org?.logoUrl || '', 'BYTESCALE', { query: LOGO })}
                              alt={org.label}
                            />
                          </LogoWrapper>

                          <span className="flex-1 truncate min-w-0 text-sm font-medium text-slate-900 dark:text-white text-left">
                            {org.label.trim()}
                          </span>

                          {isSelected && (
                          <CheckIcon className="w-5 h-5 text-[#111827] flex-shrink-0" strokeWidth={2.5} />
                          )}
                        </MenuItem>
                      );
                    })}
                  </div>
                </ScrollShadow>
              )}
            </ModalCardBody>

            <ModalCardFooter>
              <PrimaryButton
                onClick={handleConfirm}
                disabled={isBusy || !tempSelectedKey}
              >
                {isMutating ? t('common.loading') : t('organizationSelector.confirmSelection')}
              </PrimaryButton>
            </ModalCardFooter>
          </ModalCard>
        )}
      </BlockingOverlay>,
      document.body,
    );
  };

  // Pokazuj skeleton podczas ładowania auth/orgs
  if (isBusy && !isOrgsError) {
    return (
      <OrgSelectorContainer $isCollapsed={isCollapsed}>
        <TriggerButton
          type="button"
          disabled
          $isCollapsed={isCollapsed}
          $isOpen={false}
        >
          <Skeleton className="rounded-md flex-shrink-0" style={{ width: 40, height: 40 }} />
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <Skeleton className="h-[16px] w-[80%] rounded mb-2" />
              <Skeleton className="h-[12px] w-[50%] rounded" />
            </div>
          )}
        </TriggerButton>
      </OrgSelectorContainer>
    );
  }

  // Stan błędu w sidebarze (trigger) — klik = ponów. Błąd jest też w treści (Main)
  // i w modalu wyboru org.
  if (isOrgsError) {
    return (
      <OrgSelectorContainer $isCollapsed={isCollapsed}>
        <TriggerButton
          type="button"
          $isCollapsed={isCollapsed}
          $isOpen={false}
          onClick={() => organizationsQuery.refetch()}
          style={{ cursor: 'pointer' }}
        >
          <div className="flex items-center justify-center rounded-[8px] w-[30px] h-[30px] bg-amber-50 border border-amber-200 dark:bg-white/10 dark:border-white/15">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 dark:text-amber-300" />
          </div>
          {!isCollapsed && (
            <OrgInfo>
              <OrgName>{t('organization.loadError')}</OrgName>
              <span className="text-xs flex items-center gap-1 text-gray-500 dark:text-white/65">
                <ArrowPathIcon className="w-3 h-3" />
                {t('organization.clickToRetry')}
              </span>
            </OrgInfo>
          )}
        </TriggerButton>
      </OrgSelectorContainer>
    );
  }

  // Statyczny widok dla pojedynczej organizacji (tylko gdy jest aktywna)
  if (isSingleOrg && !isUserOrgEmpty) {
    return (
      <OrgSelectorContainer $isCollapsed={isCollapsed}>
        <StaticOrgDisplay $isCollapsed={isCollapsed}>
          <LogoWrapper>
            <LogoBox
              src={getImageUrl(selectedOrg?.logoUrl || '', 'BYTESCALE', { query: LOGO })}
              alt={selectedOrg?.label ?? 'Organizacja'}
              size={isCollapsed ? 36 : 30}
            />
          </LogoWrapper>

          {!isCollapsed && (
            <OrgInfo>
              <OrgName>{selectedOrg?.label ?? '—'}</OrgName>
            </OrgInfo>
          )}
        </StaticOrgDisplay>
      </OrgSelectorContainer>
    );
  }

  // Gdy mamy blocking UI, pokazujemy tylko overlay
  if (shouldShowBlockingUI) {
    return renderBlockingOverlay();
  }

  // Normalny selector dla wielu organizacji
  return (
    <>
      <OrgSelectorContainer $isCollapsed={isCollapsed}>
        <TriggerButton
          type="button"
          disabled={isMutating}
          $isCollapsed={isCollapsed}
          $isOpen={open}
          onClick={() => setOpen(true)}
        >
          <LogoWrapper>
            <LogoBox
              src={getImageUrl(selectedOrg?.logoUrl || '', 'BYTESCALE', { query: LOGO })}
              alt={selectedOrg?.label ?? 'Organizacja'}
              size={isCollapsed ? 36 : 30}
            />
          </LogoWrapper>

          {!isCollapsed && (
            <OrgInfo>
              <OrgName>{selectedOrg?.label ?? '—'}</OrgName>
            </OrgInfo>
          )}

          {!isCollapsed && (
            <ChevronDownIcon className="w-4 h-4 text-gray-400 dark:text-white/60 transition-transform flex-shrink-0" />
          )}
        </TriggerButton>
      </OrgSelectorContainer>

      {/* Standard modal for switching orgs (when user already has active org) */}
      {open && !shouldShowBlockingUI && createPortal(
        <BlockingOverlay onClick={handleCancel}>
          <ModalCard onClick={(e: any) => e.stopPropagation()}>
            {/* Sam tytuł: podtytuł powtarzał to, co widać na liście pod nim. */}
            <ModalCardHeader>
              <h2 className="text-[15px] font-light leading-tight text-slate-800 dark:text-white">
                {t('organizationSelector.changeOrganization')}
              </h2>
            </ModalCardHeader>

            <ModalCardBody>
              {(orgs?.length ?? 0) > 5 && (
              <div className="mb-2">
                <Input
                  {...inputProps}
                  variant="bordered"
                  classNames={{
                    inputWrapper: '!h-[44px] min-h-[44px] bg-white border-1 border-border-strong rounded-8 shadow-none data-[hover=true]:border-gray-400 group-data-[focus=true]:border-accent-ink',
                    input: 'text-sm',
                  }}
                  size="sm"
                  placeholder={t('organizationSelector.searchPlaceholder')}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  startContent={<MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />}
                  isClearable
                  onClear={() => setSearchQuery('')}
                />
              </div>
              )}

              {/* eslint-disable-next-line no-nested-ternary */}
              {isOrgsLoading ? (
                <div className="flex flex-col gap-1.5">
                  {[...Array(3)].map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="flex items-center gap-2.5 p-2">
                      <Skeleton className="rounded-md flex-shrink-0" style={{ width: 32, height: 32 }} />
                      <Skeleton className="h-[12px] flex-1 rounded" />
                    </div>
                  ))}
                </div>
              ) : filteredOrgs.length === 0 ? (
                <EmptyState>
                  <MagnifyingGlassIcon className="w-8 h-8 text-slate-300" />
                  <span>{t('organizationSelector.noOrganizationsFound')}</span>
                </EmptyState>
              ) : (
                <ScrollShadow className="w-full max-h-[240px]" hideScrollBar>
                  <div className="flex flex-col gap-0.5">
                    {filteredOrgs.map((org) => {
                      const isSelected = org.key === tempSelectedKey;
                      const isActive = org.key === organizationId;

                      return (
                        <MenuItem
                          key={org.key}
                          onClick={() => setTempSelectedKey(org.key)}
                          disabled={isMutating}
                          $isActive={isSelected}
                        >
                          <LogoWrapper>
                            <LogoBox
                              src={getImageUrl(org?.logoUrl || '', 'BYTESCALE', { query: LOGO })}
                              alt={org.label}
                              size={32}
                            />
                          </LogoWrapper>

                          <span className="flex-1 truncate min-w-0 text-[14px] font-medium text-slate-900 dark:text-white text-left">
                            {org.label.trim()}
                          </span>

                          {isActive && (
                          <span className="text-[11px] text-slate-400 dark:text-slate-500 flex-shrink-0 mr-1">
                            {t('organizationSelector.active')}
                          </span>
                          )}

                          {isSelected && (
                          <CheckIcon className="w-4 h-4 text-[#111827] flex-shrink-0" strokeWidth={2.5} />
                          )}
                        </MenuItem>
                      );
                    })}
                  </div>
                </ScrollShadow>
              )}
            </ModalCardBody>

            <ModalCardFooter>
              <Button
                color="default"
                variant="light"
                radius="sm"
                size="sm"
                onPress={handleCancel}
                disabled={isMutating}
              >
                {t('common.cancel')}
              </Button>
              <PrimaryButton
                onClick={handleConfirm}
                disabled={isBusy || !tempSelectedKey || (isCurrentOrg && !isUserOrgEmpty)}
              >
                {isCurrentOrg && !isUserOrgEmpty ? t('organizationSelector.alreadyActive') : t('organizationSelector.confirm')}
              </PrimaryButton>
            </ModalCardFooter>
          </ModalCard>
        </BlockingOverlay>,
        document.body,
      )}
    </>
  );
};
